// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/ObHeader.jsx
//  MICROSERVICIO ONBOARDING v3 — Header propio del flujo de diagnóstico
//
//  Existe porque "/" es la puerta de entrada de NSL: quien aterriza aquí puede
//  no ser el público del diagnóstico. Cada botón resuelve una intención distinta:
//
//    · Logo / "Para Alumnos" → la landing informativa, para quien quiere leer
//      antes de decidir.
//    · "Para Empresas"       → la LP de empresas, para el visitante B2B.
//    · "Log In"              → para quien YA hizo su onboarding y solo quiere
//      entrar a entrenar. Sin esto quedaba atrapado en el funnel.
//    · Tema claro/oscuro     → preferencia de vista, oscuro por defecto.
//
//  ⚠️  AISLAMIENTO: es un header NUEVO y propio del módulo, con clases
//      prefijadas `.ob-h`. NO reutiliza ni modifica LHeader.jsx (landing),
//      BHeader.jsx (empresas) ni Header.jsx (app autenticada). Comparte con
//      ellos únicamente la convención de tema (ver useObTheme.js).
//
//  MOBILE-FIRST: el 80% del tráfico de NSL es celular, así que la barra se
//  reparte en DOS bloques con un orden explícito, no en una lista que envuelva
//  sola:
//
//    · `ob-h-principal`   Log In y las dos preferencias. Es lo que se toca, y
//                          en teléfono comparte fila con el logo.
//    · `ob-h-secundarios` «Para Alumnos» y «Para Empresas», para quien
//                          aterrizó aquí y no es el público del diagnóstico.
//                          En teléfono bajan a su propia fila, en texto.
//
//  Rob, 26-09-2026: «este menú se ve pésimo en formatos pequeños». Y lo era:
//  medido en un teléfono daba TRES filas —logo solo, enlaces + Log In, y los
//  dos interruptores colgando solos abajo— y 156px de alto, una cuarta parte
//  de la pantalla gastada en cromo antes de leer una palabra.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useObTheme, toggleObTheme } from './useObTheme';
import { sonidoActivo, alternaSonido, escuchaSonido } from './sonidos';

export default function ObHeader() {
  const theme = useObTheme();
  const isDark = theme === 'dark';

  // El sonido se puede apagar, y la decisión se recuerda. Un flujo que suena y
  // no deja callarlo es peor que uno mudo: se abre en la oficina una vez y no
  // se vuelve a abrir.
  const [conSonido, setConSonido] = useState(sonidoActivo);
  useEffect(() => escuchaSonido(setConSonido), []);

  return (
    <header className="ob-h">
      <div className="ob-h-bar">
        <Link to="/landing" className="ob-h-logo-link" aria-label="Non Stop Learning — inicio">
          <img
            src={isDark ? '/logo-dark.png' : '/logo-light.png'}
            alt="Non Stop Learning"
            className="ob-h-logo"
          />
        </Link>

        {/* ── LO QUE SE TOCA ──────────────────────────────────────────────
            Entrar y las dos preferencias. Van juntos y pegados al logo, así
            que en un teléfono forman UNA sola fila con él. */}
        <nav className="ob-h-principal" aria-label="Tu sesión">
          <Link to="/login" className="ob-h-btn ob-h-primary">
            Log In
          </Link>

          <span className="ob-h-toggles">
            <button
              type="button"
              className="ob-h-toggle"
              data-prueba="sonido"
              aria-pressed={conSonido}
              onClick={() => setConSonido(alternaSonido())}
              aria-label={conSonido ? 'Apagar el sonido' : 'Encender el sonido'}
              title={conSonido ? 'Sonido encendido' : 'Sonido apagado'}
            >
              {conSonido ? '🔊' : '🔇'}
            </button>

            <button
              type="button"
              className="ob-h-toggle"
              onClick={toggleObTheme}
              aria-label={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
              title={isDark ? 'Tema claro' : 'Tema oscuro'}
            >
              {isDark ? '☀️' : '🌙'}
            </button>
          </span>
        </nav>

        {/* ── LAS DOS SALIDAS ─────────────────────────────────────────────
            Para quien aterrizó aquí y no es el público del diagnóstico. En
            escritorio van en el centro; en teléfono bajan enteras a su propia
            fila y se quedan en texto, sin pastilla, porque son lo secundario
            de esta barra y ocupaban tanto como lo que sí se toca. */}
        <nav className="ob-h-secundarios" aria-label="Otras secciones">
          <Link to="/landing" className="ob-h-btn ob-h-ghost">
            Para Alumnos
          </Link>
          <Link to="/business" className="ob-h-btn ob-h-ghost">
            Para Empresas
          </Link>
        </nav>
      </div>
    </header>
  );
}
